import React, { useMemo } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   COST STRUCTURE PANEL (B2)
   ----------------------------------------------------------------------------
   PANNEAU DE SYNTHÈSE GLOBALE
   - orienté matching global
   - orienté couverture
   - orienté qualité de composition
   - orienté readiness vers Quantities
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

/* ============================================================================
   STYLES PARTAGÉS LOCAUX
============================================================================ */

const S = {
  panel: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  panelHeader: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minHeight: 12,
    minWidth: 0,
    flex: "0 0 auto",
    boxSizing: "border-box",
  },

  panelTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "1.02rem",
    lineHeight: 1.1,
    color: T.text,
    fontWeight: 600,
  },

  block: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    paddingBottom: 2,
    minWidth: 0,
    flex: "0 0 auto",
    boxSizing: "border-box",
  },

  blockTitle: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.84rem",
    lineHeight: 1.12,
    color: theme?.colors?.text?.caneva || "#c24c2c",
    fontWeight: 600,
  },

  rows: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    minWidth: 0,
  },

  kvRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    alignItems: "center",
    gap: 12,
    minWidth: 0,
  },

  kvLabel: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.74rem",
    lineHeight: 1.2,
    color: T.soft,
    minWidth: 0,
  },

  kvValue: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.76rem",
    lineHeight: 1.2,
    fontWeight: 600,
    textAlign: "right",
    whiteSpace: "nowrap",
  },

  empty: {
    width: "100%",
    height: "100%",
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    gap: 22,
    overflow: "hidden",
    boxSizing: "border-box",
  },

  emptyText: {
    fontFamily: theme?.fonts?.body || "inherit",
    fontSize: "0.78rem",
    lineHeight: 1.35,
    color: T.verySoft,
  },
};

/* ============================================================================
   MOCK COST STRUCTURE
============================================================================ */

const MOCK_COST_STRUCTURE = {
  id: "cs-01",
  name: "Cost structure EPC water infrastructure",
  packageCount: 16,
  matchedArticles: 28,
  coveredCount: 6,
  partialCount: 7,
  emptyCount: 3,
  coverageRate: 84,
  dominantType: "Material / Labor",
  missingTypes: 2,
  monoTypeCount: 4,
  readyForQuantities: 10,
  pendingForQuantities: 6,
};

/* ============================================================================
   HELPERS
============================================================================ */

const getSignalColor = (tone) => {
  switch (tone) {
    case "green":
      return T.greenText;
    case "yellow":
      return T.yellowText;
    case "orange":
      return T.orangeText;
    case "red":
      return T.redText;
    default:
      return T.greyText;
  }
};

const getGlobalReadiness = (data) => {
  const coverage = data?.coverageRate || 0;
  const emptyCount = data?.emptyCount || 0;
  const missingTypes = data?.missingTypes || 0;

  if (coverage >= 90 && emptyCount === 0 && missingTypes === 0) {
    return { label: "Ready for Quantities", tone: "green" };
  }

  if (coverage >= 75) {
    return { label: "Partially ready", tone: "yellow" };
  }

  if (coverage > 0) {
    return { label: "Matching in progress", tone: "orange" };
  }

  return { label: "Not defined", tone: "grey" };
};

/* ============================================================================
   COMPOSANTS STANDARDS
============================================================================ */

const PanelBlock = ({ title, children }) => (
  <div style={S.block}>
    <div style={S.blockTitle}>{title}</div>
    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div style={S.kvRow}>
    <div style={S.kvLabel}>{label}</div>
    <div style={{ ...S.kvValue, color: valueColor }}>{value}</div>
  </div>
);

const EmptyState = () => (
  <div style={S.empty}>
    <div style={S.panelHeader}>
      <div style={S.panelTitle}>Cost Structure Overview</div>
    </div>

    <PanelBlock title="Matching summary">
      <div style={S.emptyText}>
        Initialiser le matching dans B1 pour afficher la synthèse globale de
        couverture et de préparation vers Quantities.
      </div>
    </PanelBlock>
  </div>
);

/* ============================================================================
   METRICS
============================================================================ */

const computeMetrics = (data) => {
  return {
    readiness: getGlobalReadiness(data),
  };
};

/* ============================================================================
   MAIN COMPONENT
============================================================================ */

const CostStructurePanel = ({
  selectedStructure = null,
  useMock = true,
}) => {
  const data = selectedStructure || (useMock ? MOCK_COST_STRUCTURE : null);

  const metrics = useMemo(() => {
    if (!data) return null;
    return computeMetrics(data);
  }, [data]);

  if (!data || !metrics) return <EmptyState />;

  return (
    <div style={S.panel}>
      <div style={S.panelHeader}>
        <div style={S.panelTitle}>Cost Structure Overview</div>
      </div>

      <PanelBlock title="Matching snapshot">
        <div style={S.rows}>
          <KeyValueRow
            label="Packages / items"
            value={data.packageCount ?? "-"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Matched articles"
            value={data.matchedArticles ?? "-"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Covered"
            value={data.coveredCount ?? 0}
            valueColor={T.greenText}
          />
          <KeyValueRow
            label="Partial"
            value={data.partialCount ?? 0}
            valueColor={T.yellowText}
          />
          <KeyValueRow
            label="Empty"
            value={data.emptyCount ?? 0}
            valueColor={T.orangeText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Coverage status">
        <div style={S.rows}>
          <KeyValueRow
            label="Global coverage"
            value={`${data.coverageRate || 0}%`}
            valueColor={data.coverageRate >= 75 ? T.greenText : T.yellowText}
          />
          <KeyValueRow
            label="Dominant composition"
            value={data.dominantType || "-"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Missing cost types"
            value={data.missingTypes ?? 0}
            valueColor={(data.missingTypes || 0) > 0 ? T.orangeText : T.greenText}
          />
          <KeyValueRow
            label="Mono-type structures"
            value={data.monoTypeCount ?? 0}
            valueColor={(data.monoTypeCount || 0) > 0 ? T.yellowText : T.greenText}
          />
        </div>
      </PanelBlock>

      <PanelBlock title="Readiness for Quantities">
        <div style={S.rows}>
          <KeyValueRow
            label="Ready"
            value={data.readyForQuantities ?? 0}
            valueColor={T.greenText}
          />
          <KeyValueRow
            label="To complete"
            value={data.pendingForQuantities ?? 0}
            valueColor={T.orangeText}
          />
          <KeyValueRow
            label="Global readiness"
            value={metrics.readiness.label}
            valueColor={getSignalColor(metrics.readiness.tone)}
          />
        </div>
      </PanelBlock>
    </div>
  );
};

export default CostStructurePanel;